import { Dialog } from 'ui';
import React from 'react';

import type { PracticeCourse } from '../../../types/practiceCourses';
import SubjectCard from './SubjectCard';
import useSubjectSelectionDialog from './useSubjectSelectionDialog';
import './SubjectSelectionDialog.scss';

/**
 * SubjectSelectionDialog - Popup 1 of the custom practice flow (TANAI-1869).
 * Lets the student pick a single subject from the practice courses.
 */

type SubjectSelectionDialogProps = {
  courses: PracticeCourse[];
  hasGenerationError?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (course: PracticeCourse) => void;
};

function SubjectSelectionDialog({
  courses,
  hasGenerationError = false,
  isLoading = false,
  isOpen,
  onClose,
  onConfirm,
}: Readonly<SubjectSelectionDialogProps>): JSX.Element {
  const { courseIconUrls, handleConfirm, handleSelectCourse, hasCourses, isConfirmDisabled, selectedCourseId, t } =
    useSubjectSelectionDialog({
      courses,
      isOpen,
      onConfirm,
    });

  function renderContent(): JSX.Element {
    if (isLoading) {
      return <div className="subject-selection-dialog__loading">{t('Loading...')}</div>;
    }
    if (!hasCourses) {
      return <div className="subject-selection-dialog__empty">{t('practice_more_no_subjects')}</div>;
    }
    return (
      <ul className="subject-selection-dialog__list">
        {courses.map((course) => (
          <SubjectCard
            course={course}
            iconUrl={courseIconUrls[course.id]}
            isSelected={course.id === selectedCourseId}
            key={course.id}
            onSelect={handleSelectCourse}
          />
        ))}
      </ul>
    );
  }

  return (
    <Dialog
      buttonTextSuccess={t('practice_more_select')}
      className="subject-selection-dialog"
      isDisabledSuccess={isConfirmDisabled}
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={handleConfirm}
      size="l"
      title={t('practice_more_subject_dialog_title')}
    >
      <div className="subject-selection-dialog__content">
        {hasGenerationError && (
          <p className="subject-selection-dialog__error" role="alert">
            {t('practice_more_generation_error')}
          </p>
        )}
        {renderContent()}
      </div>
    </Dialog>
  );
}

export default SubjectSelectionDialog;
